import React, { Component } from 'react';
import { Alert, Button, Modal, ProgressBar } from 'react-bootstrap';
import DesignRegistrationForm from './DesignRegistrationForm.js';
import DesignPlacementForm from './DesignPlacementForm.js';

import './DesignLibraryPopup.css';

const CHUNK_BYTES = 2 * 1024 * 1024;
const sleep = milliseconds => new Promise(resolve => setTimeout(resolve, milliseconds));

export const designFormat = name => {
    const match = /\.([^.]+)$/.exec(String(name || '').toLowerCase());
    return match && ['ifc', 'dxf'].includes(match[1]) ? match[1] : '';
};

export const validDesignFiles = files => {
    const selected = Array.from(files || []);
    if (!selected.length) throw new Error('Choose an IFC or DXF file to upload');
    if (selected.length > 2) throw new Error('Upload at most one IFC and one DXF in a package');
    const formats = selected.map(file => designFormat(file.name));
    if (formats.some(format => !format) || new Set(formats).size !== formats.length)
        throw new Error('Choose one .ifc and/or one .dxf file');
    if (selected.some(file => !Number.isFinite(file.size) || file.size <= 0 || file.size > 1024 * 1024 * 1024))
        throw new Error('Each design file must be smaller than 1 GiB');
    return selected;
};

class DesignLibraryPopup extends Component {
    constructor(props) {
        super(props);
        this.state = { show: false, recording: '', designs: [], selectedId: '', files: [], loading: false,
            busy: false, progress: 0, error: '', registrationOpen: false, placementOpen: false };
        this.pollGeneration = 0;
        this.fileInput = null;
        this.uploadController = null;
    }

    show = recording => {
        if (this.uploadController) this.uploadController.abort();
        ++this.pollGeneration;
        this.setState({ show: true, recording, designs: [], selectedId: '', files: [], loading: true,
            busy: false, progress: 0, error: '', registrationOpen: false, placementOpen: false }, this.refresh);
    }

    componentWillUnmount() {
        ++this.pollGeneration;
        if (this.uploadController) this.uploadController.abort();
    }

    close = () => {
        if (this.uploadController) this.uploadController.abort();
        ++this.pollGeneration;
        this.setState({ show: false, busy: false, registrationOpen: false, placementOpen: false });
    }

    endpoint = extra => 'design_job?recording=' + encodeURIComponent(this.state.recording) + (extra || '');

    readResponse = async response => {
        let data;
        try { data = await response.json(); } catch (error) { data = {}; }
        if (!response.ok) throw new Error(data.error || ('Design service returned HTTP ' + response.status));
        return data;
    }

    postJson = async body => this.readResponse(await fetch(this.endpoint(), {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body)
    }));

    refresh = async () => {
        if (!this.state.recording) return;
        const generation = this.pollGeneration;
        try {
            const data = await this.readResponse(await fetch(this.endpoint(), { cache: 'no-store' }));
            if (generation !== this.pollGeneration) return [];
            const designs = Array.isArray(data.designs) ? data.designs : [];
            const selectedId = this.state.selectedId && designs.some(item => item.id === this.state.selectedId)
                ? this.state.selectedId : (designs[0] ? designs[0].id : '');
            this.setState({ designs, selectedId, loading: false, error: '' });
            return designs;
        } catch (error) {
            if (generation !== this.pollGeneration) return [];
            this.setState({ loading: false, error: error.message });
            return [];
        }
    }

    handleFileChange = event => {
        const files = Array.from(event.target.files || []);
        this.setState({ files, error: '' });
    }

    upload = async resumeDesign => {
        if (this.state.busy) return;
        let files;
        try {
            files = validDesignFiles(this.state.files);
            if (resumeDesign && (!resumeDesign.sourceFiles || files.length !== resumeDesign.sourceFiles.length
                || files.some(file => !resumeDesign.sourceFiles.some(source => source.format === designFormat(file.name)
                    && source.size === file.size && source.originalName === file.name))))
                throw new Error('Reselect the original files with the same names and sizes to resume this package');
        } catch (error) { this.setState({ error: error.message }); return; }
        const generation = ++this.pollGeneration;
        const controller = new AbortController();
        this.uploadController = controller;
        const recording = this.state.recording;
        const endpoint = extra => 'design_job?recording=' + encodeURIComponent(recording) + (extra || '');
        const post = body => fetch(endpoint(), { method: 'POST', signal: controller.signal,
            headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }).then(this.readResponse);
        this.setState({ busy: true, error: '', progress: 0, registrationOpen: false, placementOpen: false });
        let designId = resumeDesign ? resumeDesign.id : '';
        const total = files.reduce((sum, file) => sum + file.size, 0);
        let uploaded = 0;
        try {
            const started = resumeDesign || await post({ action: 'uploadStart', files: files.map(file => ({ name: file.name, size: file.size })) });
            designId = started.id;
            if (generation !== this.pollGeneration) return;
            this.setState({ selectedId: designId });
            await this.refresh();
            for (const file of files) {
                const format = designFormat(file.name);
                const source = started.sourceFiles.find(item => item.format === format);
                const initialOffset = source.uploaded || 0;
                uploaded += initialOffset;
                for (let offset = initialOffset; offset < file.size; offset += CHUNK_BYTES) {
                    if (generation !== this.pollGeneration) return;
                    const chunk = file.slice(offset, Math.min(file.size, offset + CHUNK_BYTES));
                    const extra = '&action=uploadChunk&design=' + encodeURIComponent(designId)
                        + '&format=' + encodeURIComponent(format) + '&offset=' + offset;
                    await this.readResponse(await fetch(endpoint(extra), {
                        method: 'POST', signal: controller.signal, headers: { 'Content-Type': 'application/octet-stream' }, body: chunk
                    }));
                    if (generation !== this.pollGeneration) return;
                    uploaded += chunk.size;
                    this.setState({ progress: Math.floor(100 * uploaded / total) });
                }
            }
            await post({ action: 'uploadComplete', design: designId });
            if (generation !== this.pollGeneration) return;
            this.setState({ files: [], progress: 100 });
            if (this.fileInput) this.fileInput.value = '';
            await this.refresh();
            if (generation === this.pollGeneration) await this.poll(designId);
        } catch (error) {
            if (generation !== this.pollGeneration || error.name === 'AbortError') return;
            await this.refresh();
            this.setState({ error: error.message + (designId ? '. Your upload is saved; reselect the original files and resume.' : ''), busy: false });
        }
    }

    poll = async designId => {
        const generation = ++this.pollGeneration;
        for (let attempt = 0; attempt < 1800 && generation === this.pollGeneration; attempt++) {
            await sleep(1500);
            if (generation !== this.pollGeneration) return;
            const designs = await this.refresh();
            const design = designs.find(item => item.id === designId);
            if (design && ['complete', 'failed', 'cancelled'].includes(design.status)) {
                this.setState({ busy: false, progress: design.status === 'complete' ? 100 : design.progress || 0,
                    error: design.status === 'failed' ? design.message || 'Design import failed' : '' });
                return;
            }
        }
        if (generation === this.pollGeneration) this.setState({ busy: false, error: 'Design import is still processing. Refresh to check its status.' });
    }

    cancel = async designId => {
        ++this.pollGeneration;
        if (this.uploadController) this.uploadController.abort();
        try {
            await this.postJson({ action: 'cancel', design: designId });
            this.setState({ busy: false, error: '' });
            await this.refresh();
        } catch (error) { this.setState({ error: error.message }); }
    }

    saveRegistration = async registration => {
        const designId = this.state.selectedId;
        await this.postJson({ action: 'register', design: designId, ...registration });
        await this.refresh();
        this.setState({ registrationOpen: false, placementOpen: false });
    }

    selectDesign = designId => this.setState({ selectedId: designId, registrationOpen: false, placementOpen: false, error: '' });

    render() {
        const selected = this.state.designs.find(item => item.id === this.state.selectedId);
        const showRegistration = selected && selected.status === 'complete' && this.state.registrationOpen;
        return (
            <Modal show={this.state.show} onHide={this.close} dialogClassName="design-library-modal" centered>
                <Modal.Header closeButton><Modal.Title>Engineering designs</Modal.Title></Modal.Header>
                <Modal.Body>
                    {this.state.error && <Alert variant="danger" role="alert">{this.state.error}</Alert>}
                    <p className="design-library-intro">Upload IFC and/or DXF files for this recording. Place designs at their supplied UTM coordinates, then refine the alignment during review.</p>
                    <div className="design-library-upload">
                        <input aria-label="IFC or DXF design files" type="file" multiple accept=".ifc,.dxf" onChange={this.handleFileChange} disabled={this.state.busy}
                            ref={input => { this.fileInput = input; }} />
                        <Button onClick={() => this.upload()} disabled={this.state.busy || this.state.files.length === 0}>Upload and import</Button>
                    </div>
                    {this.state.files.length > 0 && <div className="design-library-file-names">{this.state.files.map(file => file.name).join(' · ')}</div>}
                    {this.state.busy && <div className="design-library-progress"><ProgressBar now={this.state.progress} label={this.state.progress + '%'} />
                        {selected && selected.status && <span>{selected.message || selected.status}</span>}
                    </div>}
                    <div className="design-library-list-heading">
                        <h3>Uploaded packages</h3>
                        <Button size="sm" variant="outline-secondary" onClick={this.refresh} disabled={this.state.loading}>Refresh</Button>
                    </div>
                    {this.state.loading ? <p>Loading designs…</p> : this.state.designs.length === 0
                        ? <p>No IFC or DXF designs have been uploaded for this recording.</p>
                        : <div className="design-library-list">{this.state.designs.map(design => (
                            <button type="button" key={design.id} className={'design-library-row' + (design.id === this.state.selectedId ? ' selected' : '')} onClick={() => this.selectDesign(design.id)}>
                                <strong>{design.name}</strong>
                                <span>{design.status === 'complete' ? (design.registered ? 'Surveyed registration' : design.reviewEnabled ? 'Ready for review · visible when this map opens' : 'Imported · choose placement')
                                    : (design.message || design.status) + (design.progress ? ' · ' + design.progress + '%' : '')}</span>
                                <small>{(design.sourceFiles || []).map(file => file.name).join(' · ')}</small>
                            </button>
                        ))}</div>}
                    {selected && selected.status === 'complete' && !showRegistration && !this.state.placementOpen && (
                        <section className="design-library-details">
                            <h3>{selected.name}</h3>
                            <div className="design-library-formats">{(selected.formats || []).map(format => (
                                <div key={format.format}>
                                    <strong>{format.format.toUpperCase()}</strong> · {(format.counts && format.counts.features || 0).toLocaleString()} features · {(format.counts && format.counts.triangles || 0).toLocaleString()} triangles
                                    {format.unsupportedCount > 0 && <span className="design-library-warning"> · {format.unsupportedCount} unsupported geometry item(s)</span>}
                                </div>
                            ))}</div>
                            <div className="design-library-sources">{(selected.sourceFiles || []).map(file => (
                                <a key={file.path} href={this.endpoint('&design=' + encodeURIComponent(selected.id) + '&action=download&path=' + encodeURIComponent(file.path))}>{file.name} · download original</a>
                            ))}</div>
                            {selected.reviewEnabled && <p>Review placement saved. Alignment can be refined at any time.</p>}
                            {selected.registered
                                ? <p className="design-library-registered">Registered. IFC and DXF layers will load with the point cloud.</p>
                                : <p className="design-library-warning">Choose placement to show this design in the map.</p>}
                            <Button onClick={() => this.setState({ placementOpen: true })}>Place for review</Button> <Button variant="outline-secondary" onClick={() => this.setState({ registrationOpen: true })}>
                                {selected.registered ? 'Edit surveyed registration' : 'Surveyed registration'}
                            </Button>
                        </section>
                    )}
                    {selected && this.state.placementOpen && <DesignPlacementForm value={selected.registration} onSave={this.saveRegistration} onCancel={() => this.setState({ placementOpen: false })} />}
                    {showRegistration && <DesignRegistrationForm key={selected.id + ':' + (selected.registrationVersion || 'new')}
                        value={selected.registration || null} onSave={this.saveRegistration}
                        onCancel={() => this.setState({ registrationOpen: false, placementOpen: false })} />}
                    {selected && selected.status === 'uploading' && !this.state.busy && <Button onClick={() => this.upload(selected)} disabled={!this.state.files.length}>Resume upload</Button>}
                    {selected && ['uploading', 'queued', 'processing'].includes(selected.status)
                        && <Button variant="outline-danger" onClick={() => this.cancel(selected.id)}>Cancel import</Button>}
                </Modal.Body>
                <Modal.Footer><Button variant="secondary" onClick={this.close}>Close</Button></Modal.Footer>
            </Modal>
        );
    }
}

export default DesignLibraryPopup;
